import React, { useCallback, useEffect, useState } from 'react';

import type { ImageAsset } from '@schibsted/frapi-schema';
import type { Statistics } from '../types.js';

import {
  getItemFromLocalStorage,
  saveItemToLocalStorage,
} from '../../../../../../../../public-src/core/js/local-storage.js';
import { useSessionData } from '../../../../../../../../public-src/core/js/schibsted-account/react.server.js';
import { usePulseContext } from '../../../../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import { getImageUrl } from '../../../../commonComponents/imageUtils.js';
import { registerPollAnswer } from '../api/register-poll-answer.js';
import {
  calcVotePercentages,
  countVotes,
  isExpired,
  removeOldStatisticsFromLocalStorage,
  shouldShowStatistics,
} from '../helpers.js';
import { Disclaimer } from './Disclaimer.js';
import { Input } from './Input.js';

interface FormProps {
  question: string;
  options: string[];
  id: string;
  requiresLogin?: boolean;
  expirationDate?: string;
  imageAsset?: ImageAsset;
  initialStatistics?: Statistics;
}

const Form: React.FC<FormProps> = ({
  question,
  initialStatistics,
  options,
  imageAsset,
  id,
  requiresLogin,
  expirationDate,
}: FormProps) => {
  const [showLoginPrompt, setShowLoginPrompt] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<Statistics | undefined>(
    initialStatistics,
  );

  const { trackClickUIElement } = usePulseContext();

  const session = useSessionData();

  const answer = statistics?.answer;
  const isPollExpired = isExpired(expirationDate);

  const imageUrl = imageAsset ? getImageUrl(imageAsset.id, 576, 320) : null;

  const submitVote = useCallback(
    async (answer: string): Promise<void> => {
      if (requiresLogin && !session) {
        setStatistics({ answer });
        saveItemToLocalStorage(`poll-vink-${id}`, JSON.stringify({ answer }));
        setShowLoginPrompt(true);

        return;
      }

      const stats = await registerPollAnswer(id, answer);

      const updatedStats = {
        ...(stats ?? initialStatistics),
        answer,
      };

      setStatistics(updatedStats);
      saveItemToLocalStorage(`poll-vink-${id}`, JSON.stringify(updatedStats));
    },
    [requiresLogin, session, id, initialStatistics],
  );

  useEffect(() => {
    removeOldStatisticsFromLocalStorage();
  }, []);

  useEffect(() => {
    if (requiresLogin && session === null) return;

    const storedStats = JSON.parse(
      getItemFromLocalStorage(`poll-vink-${id}`) ?? 'null',
    ) as Statistics | null;

    const { answer, answers } = storedStats ?? {};

    if (!answer) return;

    const hasAlreadySubmitted = !!answers;

    if (requiresLogin && !isPollExpired && !hasAlreadySubmitted) {
      if (session) {
        submitVote(answer);
      } else {
        setShowLoginPrompt(true);
      }
    }

    setStatistics((prev) => ({ ...prev, answer }));
  }, [isPollExpired, submitVote, id, requiresLogin, session]);

  const handleChange = (answer: string): void => {
    trackClickUIElement(
      {
        object: {
          id: `poll-vink-${id}-${answer}`,
          name: `poll-vink-${id}-answer:${answer}`,
          elementType: 'Input',
        },
      },
      {
        object: {
          objectId: `poll-vink-${id}-${answer}`,
          name: `poll-vink-${id}-answer:${answer}`,
          elementType: 'Input',
        },
        placement: { containerName: 'page' },
      },
    );

    submitVote(answer);
  };

  const votesCount = countVotes(statistics?.answers);

  const showStatistics = shouldShowStatistics(
    isPollExpired,
    requiresLogin,
    !!session,
    votesCount,
    answer,
  );

  return (
    <form className="poll__form">
      <legend>
        {imageUrl && (
          <figure className="poll-image">
            <img src={imageUrl} alt={imageAsset?.id} />
          </figure>
        )}
        <span>{question}</span>
      </legend>
      <fieldset>
        {options.map((option, index) => (
          <Input
            key={index}
            option={option}
            showStatistics={showStatistics}
            id={id}
            index={index}
            answer={answer}
            votePercent={calcVotePercentages(statistics, votesCount, index)}
            handleChange={handleChange}
          />
        ))}
      </fieldset>
      <Disclaimer
        showLoginPrompt={showLoginPrompt}
        isExpired={isPollExpired}
        session={session}
        requiresLogin={requiresLogin}
      />
      {showStatistics && votesCount > 0 && (
        <p className="poll-votes">{votesCount} har stemt</p>
      )}
    </form>
  );
};

export { Form };
